// src/features/trainings/TrainingsList.jsx - CORREGIDO CON FILTROS SIN ACENTOS
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaGraduationCap, FaChalkboardTeacher, FaBook } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import TableWrapper from '../../components/TableWrapper';
import FilterSelect from '../../components/FilterSelect';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';

// Hooks personalizados
import { useColumnFilter } from '../../hooks/useColumnFilter';

// Servicios y utilidades
import TrainingsService from './TrainingsService';
import { TIPOS_CAPACITACIONES, FORMATO_FECHA, getTipoDescripcion } from '../../utils/constants';

/**
 * Componente que muestra la lista de capacitaciones
 */
function TrainingsList() {
  // Estados
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [gridApi, setGridApi] = useState(null);

  const navigate = useNavigate();

  // ✅ FUNCIÓN PARA NORMALIZAR TEXTO REMOVIENDO ACENTOS/TILDES
  const normalizeText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase()
      .trim();
  }, []);

  // Value getters para campos complejos o que requieren transformación
  const typeValueGetter = useCallback((training) => {
    return getTipoDescripcion(training.type, TIPOS_CAPACITACIONES);
  }, []);

  // Configuración de filtros persistentes
  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType 
  } = useColumnFilter(trainings, 'type', typeValueGetter, 'trainings');

  const { 
    filterOptions: topicOptions, 
    activeFilters: activeTopics,
    handleOptionsUpdate: handleTopicOptionsUpdate,
    handleFiltersChange: handleTopicChange,
    filterData: filterByTopic 
  } = useColumnFilter(trainings, 'topic', null, 'trainings');

  const { 
    filterOptions: instructorOptions, 
    activeFilters: activeInstructors,
    handleOptionsUpdate: handleInstructorOptionsUpdate,
    handleFiltersChange: handleInstructorChange,
    filterData: filterByInstructor 
  } = useColumnFilter(trainings, 'instructor', null, 'trainings');

  // Cargar datos al iniciar
  useEffect(() => {
    loadTrainings();
  }, []);

  // Aplicar filtros cuando cambian los datos o los filtros activos
  useEffect(() => {
    if (!trainings || trainings.length === 0) return;
    
    // Comenzar con todos los datos originales
    let result = [...trainings];
    
    // Aplicar filtros en cadena solo si hay filtros activos
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    if (activeTopics && activeTopics.length > 0) {
      result = filterByTopic(result);
    }
    
    if (activeInstructors && activeInstructors.length > 0) {
      result = filterByInstructor(result);
    }
    
    // Actualizar el estado solo si ha cambiado el resultado
    const resultIds = result.map(item => item.id).sort().join(',');
    const currentIds = filteredTrainings.map(item => item.id).sort().join(',');
    
    if (resultIds !== currentIds) {
      setFilteredTrainings(result);
    }
  }, [
    trainings, 
    activeTypes, 
    activeTopics, 
    activeInstructors,
    filterByType,
    filterByTopic,
    filterByInstructor,
    filteredTrainings
  ]);

  // Función para cargar capacitaciones
  const loadTrainings = async () => {
    try {
      setLoading(true);
      const data = await TrainingsService.getAllTrainings();
      setTrainings(data);
      setFilteredTrainings(data);
    } catch (error) {
      console.error('Error al cargar capacitaciones:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de capacitaciones'
      });
    } finally {
      setLoading(false);
    }
  };

  // Funciones para manejar acciones de la tabla
  const handleViewTraining = useCallback((training) => {
    navigate(`/trainings/${training.id}`);
  }, [navigate]);

  const handleEditTraining = useCallback((training) => {
    navigate(`/trainings/${training.id}/edit`);
  }, [navigate]);

  const handleDeleteTraining = useCallback(async (training) => {
    if (window.confirm(`¿Está seguro que desea eliminar la capacitación "${getTipoDescripcion(training.type, TIPOS_CAPACITACIONES)}"?`)) {
      try {
        await TrainingsService.deleteTraining(training.id);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Capacitación eliminada correctamente'
        });
        loadTrainings();
      } catch (error) {
        console.error('Error al eliminar capacitación:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al eliminar la capacitación'
        });
      }
    }
  }, []);

  // Función para crear nueva capacitación
  const handleAddTraining = useCallback(() => {
    navigate('/trainings/add');
  }, [navigate]);

  // Función para cerrar snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  // ✅ CONFIGURACIÓN MEJORADA PARA FILTROS DE TEXTO SIN ACENTOS
  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true,
    // ✅ Función personalizada para comparar texto ignorando acentos
    textMatcher: ({ filterOption, value, filterText }) => {
      if (!filterText) return true;
      
      const normalizedValue = normalizeText(value);
      const normalizedFilter = normalizeText(filterText);
      
      switch (filterOption) {
        case 'contains':
          return normalizedValue.includes(normalizedFilter);
        case 'notContains':
          return !normalizedValue.includes(normalizedFilter);
        case 'equals':
          return normalizedValue === normalizedFilter;
        case 'notEqual':
          return normalizedValue !== normalizedFilter;
        case 'startsWith':
          return normalizedValue.startsWith(normalizedFilter);
        case 'endsWith':
          return normalizedValue.endsWith(normalizedFilter);
        default:
          return normalizedValue.includes(normalizedFilter);
      }
    }
  }), [normalizeText]);

  // ✅ CONFIGURACIÓN ESPECÍFICA PARA CAMPOS DE FECHA (sin normalización)
  const dateFilterParams = useMemo(() => ({
    debounceMs: 200,
    browserDatePicker: true
  }), []);

  // Definición de columnas para AG Grid con filtros mejorados
  const columnDefs = useMemo(() => [
    { 
      headerName: 'Tipo', 
      field: 'type', 
      flex: 1.5,
      valueFormatter: params => getTipoDescripcion(params.value, TIPOS_CAPACITACIONES),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    },
    { 
      headerName: 'Fecha', 
      field: 'date', 
      width: 120,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: params => {  // ✅ Para que el filtro busque en la fecha formateada
        return params.data.date ? dayjs(params.data.date).format(FORMATO_FECHA) : '';
      },
      filterParams: dateFilterParams // ✅ Configuración específica para fechas
    },
    { 
      headerName: 'Tema', 
      field: 'topic', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    },
    { 
      headerName: 'Instructor', 
      field: 'instructor', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos para nombres
    },
    { 
      headerName: 'Observaciones', 
      field: 'observations', 
      flex: 2,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    }
  ], [textFilterParams, dateFilterParams]);

  // Estilo personalizado para forzar esquinas redondeadas
  const customTableStyle = {
    borderRadius: '8px',
    overflow: 'hidden'
  };

  // Contenido memoizado para evitar renderizados innecesarios
  const renderContent = useMemo(() => {
    if (loading && filteredTrainings.length === 0) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando capacitaciones...</p>
        </div>
      );
    }

    return (
      <TableWrapper
        columnDefs={columnDefs}
        rowData={filteredTrainings}
        tableType="capacitaciones"
        onGridReady={(e) => setGridApi(e.api)}
        onView={handleViewTraining}
        onEdit={handleEditTraining}
        onDelete={handleDeleteTraining}
        style={customTableStyle}
        customGridOptions={{
          pagination: true,
          paginationPageSize: 50,
          defaultColDef: {
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true
          }
        }}
      />
    );
  }, [loading, filteredTrainings, columnDefs, handleViewTraining, handleEditTraining, handleDeleteTraining]);

  return (
    <Container fluid className="p-0">
      <Card className="rounded-card-custom" style={{ overflow: 'hidden', borderRadius: '8px' }}>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="m-0">Capacitaciones</h5>
        </Card.Header>

        {/* División principal que contiene filtros, botón y tabla */}
        <div className="bg-white">
          {/* Fila de filtros y botón Nueva Capacitación */}
          <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
            {/* Filtros dropdown */}
            <div className="d-flex gap-2">
              <FilterSelect
                options={typeOptions}
                activeFilters={activeTypes}
                onFiltersChange={handleTypeChange}
                onOptionsUpdate={handleTypeOptionsUpdate}
                title="Filtrar por tipo"
                filterKey="trainings-types"
                icon={<FaGraduationCap />}
              />
              
              <FilterSelect
                options={topicOptions}
                activeFilters={activeTopics}
                onFiltersChange={handleTopicChange}
                onOptionsUpdate={handleTopicOptionsUpdate}
                title="Filtrar por tema"
                filterKey="trainings-topics"
                icon={<FaBook />}
              />
              
              <FilterSelect
                options={instructorOptions}
                activeFilters={activeInstructors}
                onFiltersChange={handleInstructorChange}
                onOptionsUpdate={handleInstructorOptionsUpdate}
                title="Filtrar por instructor"
                filterKey="trainings-instructors"
                icon={<FaChalkboardTeacher />}
              />
            </div>
            
            {/* Botones de acción */}
            <div className="d-flex gap-2">
              <ExportCsvButton gridApi={gridApi} moduleName="capacitaciones" />
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddTraining}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-1" /> Nueva Capacitación
              </Button>
            </div>
          </div>

          {/* Tabla de capacitaciones */}
          <Card.Body className="p-0">
            {renderContent}
          </Card.Body>
        </div>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />

      {/* Estilos CSS inline específicos para este componente */}
      <style jsx="true">{`
        .rounded-card-custom .ag-root-wrapper {
          border-radius: 8px !important;
        }
        .rounded-card-custom .ag-header {
          border-top-left-radius: 8px !important;
          border-top-right-radius: 8px !important;
        }
        .rounded-card-custom .ag-paging-panel {
          border-bottom-left-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
        }
      `}</style>
    </Container>
  );
}

export default TrainingsList;